/** Diagnostics (UX-SPEC §9): plain definition lists and tables from `diagnosticsSections`, and a Copy button. */
import { useState } from 'preact/hooks';
import { diagnosticsSections, diagnosticsText } from '../../shared/diagnostics.ts';
import type { ViewModel } from '../../shared/viewmodel.ts';
import { ChannelTerm } from '../components/Term.tsx';
import { isChannelId } from '../model.ts';
import { hashFor } from '../router.ts';
import { wallMs } from '../state.ts';
import { DIAG_EXPLAIN } from '../strings-explain.ts';
import { DIAG } from '../strings.ts';

/** The caption under each section heading (P14), by section id; sections without one get none. */
const SECTION_CAPTION: Record<string, string> = {
	model: DIAG_EXPLAIN.model,
	document: DIAG_EXPLAIN.document,
	intervals: DIAG_EXPLAIN.intervals,
	timings: DIAG_EXPLAIN.timing,
	abstentions: DIAG_EXPLAIN.abstained,
};

/** An abstained channel's row header: "ID name" with the explainer popover, and a separate link to its page. */
function AbstainedChannel({ text }: { text: string }) {
	const id = text.split(' ')[0];
	if (!isChannelId(id)) return <>{text}</>;
	const name = text.slice(id.length + 1) || undefined;
	return (
		<>
			<ChannelTerm id={id} name={name} />{' '}
			<a
				class="diag-open"
				href={hashFor('channel', { id })}
				aria-label={DIAG_EXPLAIN.openChannel(id)}
			>
				{DIAG_EXPLAIN.openChannel(id)}
			</a>
		</>
	);
}

export function Diagnostics({ vm }: { vm: ViewModel }) {
	const sections = diagnosticsSections(vm, { wallMs: wallMs.value ?? undefined });
	const [copied, setCopied] = useState(false);
	const copy = async () => {
		try {
			await navigator.clipboard.writeText(diagnosticsText(sections));
			setCopied(true);
			window.setTimeout(() => setCopied(false), 2500);
		} catch {
			setCopied(false);
		}
	};
	return (
		<div class="wrap" data-testid="diagnostics">
			<div class="sec-head">
				<h1>{DIAG.title}</h1>
				<button type="button" class="btn btn-secondary" onClick={() => void copy()}>
					{DIAG.copy}
				</button>
				<span class="dim" aria-live="polite">
					{copied ? DIAG.copied : ''}
				</span>
			</div>
			{sections.map((s) => (
				<section
					key={s.id}
					id={`diag-${s.id}`}
					aria-labelledby={`diag-h-${s.id}`}
					data-testid={`diag-${s.id}`}
				>
					<h2 id={`diag-h-${s.id}`}>{s.title}</h2>
					{SECTION_CAPTION[s.id] ? <p class="caption">{SECTION_CAPTION[s.id]}</p> : null}
					{s.rows ? (
						<dl class="diag-rows">
							{s.rows.map(([k, v]) => (
								<div key={k} class="diag-row">
									<dt>{k}</dt>
									<dd>{v}</dd>
								</div>
							))}
						</dl>
					) : null}
					{s.table ? (
						<div class="tablewrap">
							<table>
								<caption class="visually-hidden">{s.title}</caption>
								<thead>
									<tr>
										{s.table.head.map((h) => (
											<th key={h} scope="col">
												{h}
											</th>
										))}
									</tr>
								</thead>
								<tbody>
									{s.table.rows.map((r) => (
										<tr key={r.join('|')}>
											{r.map((c, i) =>
												i === 0 ? (
													<th key={`${i}-${c}`} scope="row" class="mono">
														{s.id === 'abstentions' ? <AbstainedChannel text={c} /> : c}
													</th>
												) : (
													<td key={`${i}-${c}`}>{c}</td>
												)
											)}
										</tr>
									))}
								</tbody>
							</table>
						</div>
					) : null}
					{s.lines ? (
						<ul class="diag-lines">
							{s.lines.map((l) => (
								<li key={l}>{l}</li>
							))}
						</ul>
					) : null}
				</section>
			))}
		</div>
	);
}
